import { faCircleCheck, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ProcessingPipelineUpdates from './ProcessingPipelineUpdates';
import WaitingApprovalScreen from './WaitingApprovalScreen';
import { DurationEstimate, RunStatus } from '../../../../../../../backend/src/types/types';
import {
  PipelineProcessingStageDescriptions,
  PipelineProcessingStages,
  ProcessingStatus,
} from '@/constants/pipelineStatus';

const getPipelineStatusIcon = (pipelineStatus: ProcessingStatus, index: number) => {
  switch (true) {
    case PipelineProcessingStages.indexOf(pipelineStatus) > index:
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
          }}
          className="flex items-center justify-center"
        >
          <FontAwesomeIcon icon={faCircleCheck} className="text-brandSecondary h-6 w-6" />
        </motion.div>
      );
    case PipelineProcessingStages.indexOf(pipelineStatus) === index:
      return <FontAwesomeIcon icon={faCircleNotch} className="fa-spin h-3 w-3 text-blue-500" />;
    case PipelineProcessingStages.indexOf(pipelineStatus) < index:
      return <div className="h-2 w-2 rounded-full bg-gray-300" />;
    default:
      return <div className="h-2 w-2 rounded-full bg-gray-300" />;
  }
};

const ProcessingPipeline = ({
  casePipelineStatus,
  runId,
  pageCount,
  durationEstimate,
}: {
  casePipelineStatus: RunStatus;
  runId?: string;
  pageCount?: number;
  completedAtEstimate?: Date | null;
  durationEstimate?: DurationEstimate | null;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  return (
    <div className="mt-6 flex gap-24">
      <div className="relative">
        <div className="absolute top-0 left-3 h-full w-[2px] bg-gray-200" />

        <div className="flex flex-col space-y-6">
          {PipelineProcessingStages.map((status: ProcessingStatus, index: number) => {
            if (status === RunStatus.AwaitingApproval) {
              return null;
            }

            return (
              <div key={status} className="flex items-center">
                <div className="relative z-10 mr-4 flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                  {getPipelineStatusIcon(casePipelineStatus as ProcessingStatus, index)}
                </div>
                <div
                  className={`w-80 rounded border text-center ${
                    PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus) === index
                      ? 'border-blue-200 bg-blue-50/30 shadow-[0_0_15px_rgba(59,130,246,0.15)]'
                      : ''
                  }`}
                >
                  <div
                    className={`rounded-t-sm border-b py-1 text-sm ${
                      PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus) === index
                        ? 'bg-blue-100 text-blue-700'
                        : PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus) > index
                          ? 'bg-brandSecondary/20 text-green-700'
                          : 'bg-gray-100'
                    }`}
                  >
                    {PipelineProcessingStageDescriptions[status].title}
                  </div>
                  <div className="px-4 py-1.5 text-xs text-gray-500">
                    {PipelineProcessingStageDescriptions[status].description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {casePipelineStatus === RunStatus.AwaitingApproval && runId ? (
        <WaitingApprovalScreen
          runId={runId}
          pageCount={pageCount}
          onApprovalDenied={() => {
            navigate(`/app/chronos/case-creator?caseId=${caseId}`);
          }}
          durationEstimate={durationEstimate}
        />
      ) : (
        <ProcessingPipelineUpdates
          pipelineStepTitle={
            PipelineProcessingStageDescriptions[casePipelineStatus as ProcessingStatus]?.title ||
            'Processing will begin shortly...'
          }
          pipelineStepSubTitle={
            PipelineProcessingStageDescriptions[casePipelineStatus as ProcessingStatus]?.subTitle ||
            'About to begin processing...'
          }
          stepNumber={
            PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus) >
            PipelineProcessingStages.indexOf(RunStatus.AwaitingApproval)
              ? PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus)
              : PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus) + 1
          }
        />
      )}
    </div>
  );
};

export default ProcessingPipeline;
